<script lang="ts">
  import { page } from "$app/state";

  import { createCollapsible, melt } from "@melt-ui/svelte";
  import { Link, MinusIcon, PlusIcon } from "lucide-svelte";
  import type { Icon, IconProps } from "lucide-svelte";
  import { type Component, onMount, type Snippet } from "svelte";
  import { slide } from "svelte/transition";

  import { makeToast } from "$lib/toast";

  interface Props {
    headline: string;
    icon?: typeof Icon | Component<IconProps>;
    children?: Snippet;
    id?: string;
  }

  let { headline, icon = undefined, children, id }: Props = $props();

  let {
    elements: { root, content, trigger },
    states: { open },
  } = createCollapsible();
  onMount(() => {
    open.set(page.url.hash === `#${id}`);
  });
</script>

<div use:melt={$root}>
  <hr class="border-light-green/30 marketing-contained-negate-ml" />
  <div class="py-5 scroll-mt-32" {id}>
    <div class="flex items-baseline group">
      {#if id}
        <button
          onclick={async () => {
            const url = new URL(window.location.href);
            url.hash = id ?? "";
            await navigator.clipboard.writeText(url.toString());
            makeToast({ kind: "info", message: "URL copied to clipboard" });
          }}
          class="block -ml-8 pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <Link class="w-4 h-4 text-brand-green/50" />
        </button>
      {/if}
      <button use:melt={$trigger} class="flex w-full gap-5 items-start">
        {#if icon}
          {@const SvelteComponent = icon}
          <SvelteComponent class="text-brand-green shrink-0" />
        {/if}
        <p class="marketing-h4 pb-1 text-left">{headline}</p>
        {#if $open}
          <MinusIcon class="ml-auto shrink-0" />
        {:else}
          <PlusIcon class="ml-auto shrink-0" />
        {/if}
      </button>
    </div>
    {#if $open}
      <div
        use:melt={$content}
        transition:slide
        class="pt-3 text-base text-light-green/60"
      >
        {@render children?.()}
      </div>
    {/if}
  </div>
</div>
